import { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CircularProgress } from '@mui/material'
import {
  getWhitelabelDetails,
  uploadCompanyFavIcon,
  uploadBannerImage,
  uploadLogoImage,
  updateCompanyProfile,
  getCompanyProfile,
} from 'api'
import { dispatchWhiteLabelTheme } from 'actions/whitelabel'
import {
  Card,
  Container,
  HeaderBar,
  TypoGraph,
  ColorPicker,
  Icon,
  Button,
  Divider,
  Toast as toast,
} from 'components'
import { TextField } from 'components/controllers'
import strings from 'strings'
import styles from './whiteLabel.module.scss'
import { reportException } from 'tools/logs'
import { isViewOnly } from 'tools/auth'
import { IMAGE_ACCEPT, validateImageFile } from 'tools/imageUpload'

const t = strings.WHITE_LABEL

interface IWhileLabelProps {}

const WhileLabel: React.FC<IWhileLabelProps> = (props) => {
  const primaryColor = useSelector(
    (state: any) => state?.whiteLabel?.theme?.primary
  )
  const [files, setFiles] = useState<any>({
    logoImage: {
      file: [],
      loading: false,
    },
    bannerImage: {
      file: [],
      loading: false,
    },
    favImage: {
      file: [],
      loading: false,
    },
  })
  const [logoUrl, setLogoUrl] = useState('')
  const [bannerUrl, setBannerurl] = useState('')
  const [favUrl, setFavUrl] = useState('')
  const [color, setColor] = useState(primaryColor)
  const [openColorPicker, setOpenColorPicker] = useState(false)
  const [loading, setLoading] = useState(false)
  const [submitLoader, setSubmitLoader] = useState(false)

  const companyName = localStorage.getItem('companyName') || ''

  const dispatch = useDispatch()

  const dropzoneLogoRef = useRef<any>(null)
  const dropzoneBannerRef = useRef<any>(null)
  const dropzoneFavRef = useRef<any>(null)

  const dropzoneData = {
    logoImage: {
      ref: dropzoneLogoRef,
      location: 'logoImage',
      size: '3000px * 3000px',
      dialog: 'logo',
      title: 'Logo',
    },
    bannerImage: {
      ref: dropzoneBannerRef,
      location: 'bannerImage',
      size: '3000px * 3000px',
      dialog: 'banner',
      title: 'Banner',
    },
    favImage: {
      ref: dropzoneFavRef,
      location: 'favImage',
      size: '3000px * 3000px',
      dialog: 'fav',
      title: 'Favicon',
    },
  }

  const handleColorChange = (color: string) => {
    setColor(color)
    dispatch(dispatchWhiteLabelTheme({ primary: color }))
  }

  const handleColorCodeChange = (e: any) => {
    const value = e.target.value
    setColor(value)
  }

  /** Triggers on first render to get and set the company profile */
  useEffect(() => {
    setLoading(true)
    getWhitelabelDetails()
      .then((res: any) => {
        const companyProfile = res?.companyProfile
        setLogoUrl(companyProfile?.logo)
        setBannerurl(companyProfile?.banner)
        setFavUrl(companyProfile?.favicon)
        setColor(companyProfile?.colourCode)
        dispatch(
          dispatchWhiteLabelTheme({ primary: companyProfile?.colourCode })
        )
      })
      .catch((error: any) => {
        reportException(error)
      })
      .finally(() => setLoading(false))
  }, [dispatch])

  /** submits the form payload to update company profile */
  const handleUpdateWhiteLabel = async () => {
    setSubmitLoader(true)
    const payLoad = {
      banner: bannerUrl,
      logo: logoUrl,
      favicon: favUrl,
      colourCode: color,
    }
    updateCompanyProfile(payLoad)
      .then((res: any) => {
        getCompanyProfile(dispatch)
        return res
      })
      .then((res) => {
        toast({ id:"companyProfileToast",title: 'Success', subTitle: res?.message })
      })
      .catch((error: any) => {
        reportException(error)
      })
      .finally(() => setSubmitLoader(false))
  }

  /** To update the loading state of the specific image uploader */
  const handleUploadLoading = (location: any, bool: boolean) => {
    setFiles((prev: any) => ({
      ...prev,
      [location]: {
        ...prev[location],
        loading: bool,
      },
    }))
  }

  const handleFileUploads = async (location: any, file: any) => {
    const formData = new FormData()
    formData.append('file', file[0])
    if (location === 'logoImage') {
      handleUploadLoading(location, true)
      uploadLogoImage(formData)
        .then((res: any) => {
          setLogoUrl(`${res?.LogoUrl}?${Math.floor(Math.random() * 10000000)}`)
        })
        .catch((error: any) => {
          reportException(error)
        })
        .finally(() => handleUploadLoading(location, false))
    }
    if (location === 'bannerImage') {
      handleUploadLoading(location, true)
      uploadBannerImage(formData)
        .then((res: any) => {
          setBannerurl(
            `${res?.BannerUrl}?${Math.floor(Math.random() * 10000000)}`
          )
        })
        .catch((error: any) => {
          reportException(error)
        })
        .finally(() => handleUploadLoading(location, false))
    }
    if (location === 'favImage') {
      handleUploadLoading(location, true)
      uploadCompanyFavIcon(formData)
        .then((res: any) => {
          setFavUrl(
            `${res?.FaviconUrl}?${Math.floor(Math.random() * 10000000)}`
          )
        })
        .catch((error: any) => {
          reportException(error)
        })
        .finally(() => handleUploadLoading(location, false))
    }
  }

  const DropzoneComponent = (props: any) => {
    const { data, fileData, url, icon } = props
    const inputRef = useRef<HTMLInputElement>(null)
    console.log('fileData', fileData, url)

    const validateImageSize = (file: File): Promise<boolean> => {
      return new Promise((resolve) => {
        const img = new Image()
        const url = URL.createObjectURL(file)
        img.onload = () => {
          URL.revokeObjectURL(url)
          const [width, height] = data.size.split('*').map((dim: string): number => parseInt(dim.trim()))
          const isValid = img.width <= width && img.height <= height
          if (!isValid) {
            toast({
              id: "imageSizeError",
              title: 'Error',
              type: 'error',
              subTitle: `Image dimensions should be ${data.size} or smaller`
            })
          }
          resolve(isValid)
        }
        img.onerror = () => {
          URL.revokeObjectURL(url)
          resolve(true)
        }
        img.src = url
      })
    }

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files[0]) {
        const file = event.target.files[0]
        const error = validateImageFile(file)
        if (error) {
          toast({
            id: 'imageFileError',
            type: 'error',
            title: 'Error',
            subTitle: error,
          })
          event.target.value = ''
          return
        }
        const isValid = await validateImageSize(file)
        if (isValid) {
          handleFileUploads(data.location, event.target.files)
        }
        // Reset the input so the same file can be selected again
        event.target.value = ''
      }
    }

    return (
      <Container className={styles.mtb1}>
        <Divider title={data.title} />
        {url && !fileData.loading && <Container>{icon}</Container>}
        <input
          type="file"
          ref={inputRef}
          style={{ display: 'none' }}
          accept={IMAGE_ACCEPT}
          data-testid="image-file-input"
          onChange={handleFileSelect}
        />
        {url && !fileData.loading ? (
          !isViewOnly() && (
            <Button
              id={"changeImage"}
              data-testid="change-image-btn"
              variant="outlined"
              label="Change Image"
              onClick={() => inputRef.current?.click()}
              className={styles.changeBtn}
            />
          )
        ) : (
          <div
            className={styles.dropzoneContainer}
            onClick={() => !isViewOnly() && inputRef.current?.click()}
          >
            {fileData.loading ? (
              <Container className={styles.dropZoneLoader}>
                <CircularProgress />
              </Container>
            ) : (
              <Container className={styles.fileUploadText}>
                <Icon name="icon_uploader" size={46} />
                <TypoGraph
                  content={'Drag and Drop or Browse your file'}
                  className={styles.dropzoneText}
                />
                <TypoGraph
                  variant="body2"
                  sx={{ fontWeight: 400 }}
                  content={`Maximum image size: ${data.size}`}
                  className={styles.dropzoneText}
                />
                <TypoGraph
                  variant="body2"
                  sx={{ fontWeight: 400 }}
                  content={t.IMAGE_REQUIREMENTS}
                  className={styles.dropzoneText}
                />
              </Container>
            )}
          </div>
        )}
      </Container>
    )
  }

  return (
    <Container>
      <HeaderBar
        title={t.WHITE_LABEL}
        {...(!isViewOnly() && {
          primaryBtnProps: {
            label: strings.UPDATE,
            'data-testid': 'update-whitelabel-btn',
            loading: submitLoader,
            onClick: handleUpdateWhiteLabel,
          },
        })}
      />
      <Card>
        {loading ? (
          <Container className={styles.loader}>
            <CircularProgress />
          </Container>
        ) : (
          <>
            <TypoGraph content={t.WHITE_LABEL} variant="h6" />
            <Container className={styles.formContainer}>
              <Container>
                <TextField
                  label="Organization Name"
                  value={companyName}
                  disabled
                />
                <Container className={styles.themeContainer}>
                  <TextField
                    label="Theme Color"
                    value={color}
                    onChange={(e) => handleColorCodeChange(e)}
                    onClick={() => !isViewOnly() && setOpenColorPicker(!openColorPicker)}
                    disabled={isViewOnly()}
                  />
                  {!isViewOnly() && (
                    <ColorPicker
                      onclose={() => setOpenColorPicker(false)}
                      sendSelectedColor={handleColorChange}
                      openColorPicker={openColorPicker}
                      defaultColor={primaryColor}
                    />
                  )}
                </Container>
                <DropzoneComponent
                  data={dropzoneData.logoImage}
                  fileData={files.logoImage}
                  url={logoUrl}
                  icon={
                    <Icon
                      type="images"
                      size={400}
                      height={200}
                      url={logoUrl}
                      boxClassName={styles.previewImage}
                    />
                  }
                />
                <DropzoneComponent
                  data={dropzoneData.bannerImage}
                  fileData={files.bannerImage}
                  url={bannerUrl}
                  icon={
                    <Icon
                      type="images"
                      size={400}
                      height={200}
                      url={bannerUrl}
                      boxClassName={styles.previewImage}
                    />
                  }
                />
                <DropzoneComponent
                  data={dropzoneData.favImage}
                  fileData={files.favImage}
                  url={favUrl}
                  icon={
                    <Icon
                      type="images"
                      size={400}
                      height={200}
                      url={favUrl}
                      boxClassName={styles.previewImage}
                    />
                  }
                />
              </Container>
            </Container>
          </>
        )}
      </Card>
    </Container>
  )
}

export default WhileLabel
